import axios from 'axios';
import Pusher from 'pusher-js/dist/web/pusher';

let configPromise = null;
let pusherSingleton = null;

export const REALTIME_CHANNELS = {
  adminRefresh: 'private-admin-refresh',
  adminDbLogs: 'private-admin-dblogs',
  adminCron: 'private-admin-cron',
  publicMetrics: 'public-metrics',
  adminDb: 'private-admin-db',
  publicVmtSends: 'public-vmt-sends',
};

export async function fetchRealtimeConfig() {
  if (!configPromise) {
    configPromise = axios
      .get('/api/realtime/config')
      .then((res) => res.data)
      .catch((err) => {
        // Don't permanently brick realtime if the API wasn't reachable during initial load.
        // Clear the cached promise so a later attempt can retry.
        configPromise = null;
        return { enabled: false, error: err?.message || 'Failed to fetch realtime config' };
      });
  }
  return configPromise;
}

function normalizeConfig(raw) {
  if (!raw || typeof raw !== 'object') return { enabled: false };
  const port = raw.port == null ? null : Number(raw.port);
  return {
    enabled: Boolean(raw.enabled && raw.key && raw.host),
    key: raw.key,
    host: raw.host,
    // Newer pusher-js versions require a cluster even when using custom wsHost.
    // Provide a harmless default; wsHost/wsPort override the actual connection target.
    cluster: raw.cluster || 'mt1',
    port: Number.isFinite(port) ? port : 443,
    useTLS: Boolean(raw.useTLS),
    authEndpoint: raw.authEndpoint || '/api/realtime/auth',
  };
}

export async function getRealtimeClient() {
  const cfg = normalizeConfig(await fetchRealtimeConfig());
  if (!cfg.enabled) {
    console.info('[Realtime] disabled', { enabled: cfg.enabled, host: cfg.host, port: cfg.port });
    return { enabled: false, config: cfg, pusher: null };
  }

  if (!pusherSingleton) {
    console.info('[Realtime] initializing Pusher', { host: cfg.host, port: cfg.port, useTLS: cfg.useTLS });
    console.info('[Realtime] transport support', { hasWebSocket: typeof WebSocket !== 'undefined' });
    pusherSingleton = new Pusher(cfg.key, {
      cluster: cfg.cluster,
      wsHost: cfg.host,
      wsPort: cfg.port,
      wssPort: cfg.port,
      forceTLS: cfg.useTLS,
      // Pusher expects the transport name to be "ws" even when using TLS (wss).
      enabledTransports: ['ws'],
      disableStats: true,
      authEndpoint: cfg.authEndpoint,
      // Ensure cookies are sent to the backend auth endpoint.
      auth: {
        headers: {},
      },
    });
    pusherSingleton.connection.bind('state_change', (state) => {
      console.info('[Realtime] connection state', state);
    });
    pusherSingleton.connection.bind('error', (err) => {
      console.error('[Realtime] connection error', err);
    });
    console.info('[Realtime] initial state', pusherSingleton.connection?.state || 'unknown');
    setTimeout(() => {
      console.info('[Realtime] state after 1s', pusherSingleton.connection?.state || 'unknown');
    }, 1000);
    try {
      pusherSingleton.connect();
    } catch (err) {
      console.error('[Realtime] connect() failed', err);
    }
  }

  return { enabled: true, config: cfg, pusher: pusherSingleton };
}

export function getRealtimeSingletonUnsafe() {
  return pusherSingleton;
}
